import React from 'react';
import { Divider, InputNumber, Radio, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { LONG_INPUT_OPTIONS, PREDICTION_OPTIONS } from './runtimeOptions';
import {
  getTrainedPredictionStrategy,
  isNerTaskType,
  isVllmTaskType,
  taskUsesChunkedAggregation,
} from './taskRuntimeDefaults';
import {
  getPredictionStrategyLabel,
  predictionStrategyDisablesParallel,
} from '@/config/runtimeStrategyOptions';
import InsightNotice from './InsightNotice';

function SectionHeading({ title, description, tooltip }) {
  return (
    <div className="configure-deployment-modal__section-heading">
      <div className="configure-deployment-modal__section-title-row">
        <h3 className="configure-deployment-modal__section-title">{title}</h3>
        {tooltip ? (
          <Tooltip title={tooltip}>
            <QuestionCircleOutlined className="configure-deployment-modal__label-icon" />
          </Tooltip>
        ) : null}
      </div>
      {description ? (
        <p className="configure-deployment-modal__section-desc">{description}</p>
      ) : null}
    </div>
  );
}

function TwoColumnRadioGroup({
  options,
  value,
  onChange,
  disabled = false,
  disabledValues = null,
  rows = null,
}) {
  const disabledSet = disabledValues ? new Set(disabledValues) : null;
  const optionsByValue = Object.fromEntries(options.map((opt) => [opt.value, opt]));

  const rowGroups = rows
    ? rows.map((rowValues) =>
        rowValues.map((rowValue) => optionsByValue[rowValue]).filter(Boolean)
      )
    : [options];

  const renderOption = (opt) => (
    <div key={opt.value} className="configure-deployment-modal__radio-column">
      <div className="configure-deployment-modal__radio-option">
        <div className="configure-deployment-modal__radio-header">
          <Radio
            value={opt.value}
            disabled={disabledSet ? disabledSet.has(opt.value) : false}
          >
            {opt.title}
          </Radio>
          <Tooltip title={opt.description}>
            <QuestionCircleOutlined className="configure-deployment-modal__label-icon" />
          </Tooltip>
        </div>
        <p className="configure-deployment-modal__radio-desc">{opt.description}</p>
      </div>
    </div>
  );

  return (
    <Radio.Group
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{ display: 'block', width: '100%' }}
    >
      <div
        className={
          rows
            ? 'configure-deployment-modal__radio-rows'
            : 'configure-deployment-modal__radio-columns'
        }
      >
        {rows
          ? rowGroups.map((rowOpts, index) => (
              <div
                key={rowOpts.map((opt) => opt.value).join('-') || index}
                className="configure-deployment-modal__radio-columns"
              >
                {rowOpts.map(renderOption)}
              </div>
            ))
          : options.map(renderOption)}
      </div>
    </Radio.Group>
  );
}

export default function TaskRuntimeConfigPanel({
  training,
  runtimeConfig,
  onRuntimeConfigChange,
  instancesCapErrorMessage = null,
  onInstancesFieldChange,
}) {
  const vllmTask = isVllmTaskType(training);
  const nerTask = isNerTaskType(training);
  const usesChunkedAggregation = taskUsesChunkedAggregation(training, runtimeConfig);

  if (vllmTask) {
    return null;
  }

  const handleInstancesChange = (value) => {
    onInstancesFieldChange?.();
    onRuntimeConfigChange({
      ...runtimeConfig,
      instance_group_count: value ?? 1,
    });
  };

  const handleLongInputChange = (value) => {
    onRuntimeConfigChange({
      ...runtimeConfig,
      long_input_processing: value,
    });
  };

  const handlePredictionChange = (value) => {
    onRuntimeConfigChange({
      ...runtimeConfig,
      prediction_strategy: value,
    });
  };

  const truncationSelected = predictionStrategyDisablesParallel(
    runtimeConfig.prediction_strategy
  );
  const showLongInputParallelWarning =
    truncationSelected && runtimeConfig.long_input_processing === 'parallel';

  const trainedPredictionStrategy = getTrainedPredictionStrategy(training);
  const selectedPredictionStrategy = runtimeConfig.prediction_strategy;
  const showPredictionStrategyWarning =
    trainedPredictionStrategy &&
    selectedPredictionStrategy &&
    selectedPredictionStrategy !== trainedPredictionStrategy;
  const trainedLabel = getPredictionStrategyLabel(trainedPredictionStrategy);
  const selectedLabel = getPredictionStrategyLabel(selectedPredictionStrategy);

  return (
    <>
      <div className="configure-deployment-modal__instances-block">
        <div className="configure-deployment-modal__row-control">
          <div className="configure-deployment-modal__row-control-text">
            <div className="configure-deployment-modal__section-title-row">
              <h3 className="configure-deployment-modal__section-title">Instances</h3>
              <Tooltip title="Number of Triton model instances on this machine">
                <QuestionCircleOutlined className="configure-deployment-modal__label-icon" />
              </Tooltip>
            </div>
            <p className="configure-deployment-modal__row-subtitle">
              Set the number of instances for this task.
            </p>
            {instancesCapErrorMessage ? (
              <p className="configure-deployment-modal__instances-error">
                {instancesCapErrorMessage}
              </p>
            ) : null}
          </div>
          <InputNumber
            className="configure-deployment-modal__instances-input"
            min={1}
            value={runtimeConfig.instance_group_count}
            onChange={handleInstancesChange}
            status={instancesCapErrorMessage ? 'error' : undefined}
          />
        </div>
      </div>

      <Divider className="configure-deployment-modal__divider" />

      <div className="configure-deployment-modal__radio-section">
        <SectionHeading
          title="Long input processing"
          description="Choose how long inputs should be processed."
        />
        <TwoColumnRadioGroup
          options={LONG_INPUT_OPTIONS}
          value={runtimeConfig.long_input_processing}
          onChange={handleLongInputChange}
        />
        {showLongInputParallelWarning ? (
          <InsightNotice
            content="Truncation produces a single chunk, so parallel chunk processing does not apply."
            explanation={
              'With a truncation prediction strategy, inputs are processed as one chunk regardless of this setting. Parallel processing only affects models that split long inputs into multiple chunks.'
            }
          />
        ) : null}
      </div>

      {nerTask ? null : (
        <>
          <Divider className="configure-deployment-modal__divider" />

          <div className="configure-deployment-modal__radio-section">
            <SectionHeading
              title="Prediction strategy"
              description="Choose how predictions are calculated across input chunks."
            />
            <TwoColumnRadioGroup
              options={PREDICTION_OPTIONS}
              rows={[['mean', 'max'], ['truncate_start', 'truncate_end']]}
              value={runtimeConfig.prediction_strategy}
              onChange={handlePredictionChange}
            />
            {showPredictionStrategyWarning ? (
              <InsightNotice
                content={`This model version was trained with '${trainedLabel}'. You selected '${selectedLabel}' for deployment.`}
                explanation={
                  'Deploying with a different prediction strategy than the one used during training may change how inputs are processed and affect model accuracy.'
                }
              />
            ) : null}
          </div>
        </>
      )}
    </>
  );
}
